// Per-photo framing set in the admin: for each screen size, the point of the photo that
// stays in view (x / y in %), a zoom (1–3) and an optional box shape (SHAPE_RATIO). Stored next to the photo:
//   MEDIA object (hero.media)         → hero.media.framing
//   list item {url, alt}             → item.framing
//   plain photo field (hero.image)   → hero.imageFraming
// { desktop: {x, y, zoom}, tablet: {…}, phone: {…} } — an empty size uses the next larger
// one (phone → tablet → desktop → centre, 1×).
// Screen sizes: phone ≤ 600px, tablet 601–1024px, desktop ≥ 1025px (src/index.css .is-framed).

export const DEVICES = ["desktop", "tablet", "phone"];
const CENTRE = { x: 50, y: 50, zoom: 1 };

// Box shapes a photo can take on one screen size. "auto" (or no shape) keeps the box as the
// design sizes it; the others set the box's aspect ratio (CSS aspect-ratio).
export const SHAPE_RATIO = { wide: "16 / 9", standard: "4 / 3", square: "1 / 1", tall: "3 / 4" };
export const SHAPES = ["auto", ...Object.keys(SHAPE_RATIO)];

const num = (v, min, max, dflt) => {
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : dflt;
};
// A size's own shape; a size that doesn't set one inherits the larger size's (base).
const shapeOf = (f, base) => {
  if (!f || !Object.prototype.hasOwnProperty.call(f, "shape")) return base.shape;
  return SHAPE_RATIO[f.shape] ? f.shape : undefined;
};
const clean = (f, base) => ({
  x: num(f?.x, 0, 100, base.x),
  y: num(f?.y, 0, 100, base.y),
  zoom: num(f?.zoom, 1, 3, base.zoom),
  shape: shapeOf(f, base),
});

/** The framing actually used on each screen size, fallbacks applied. */
export function effectiveFraming(framing) {
  const desktop = clean(framing?.desktop, CENTRE);
  const tablet = clean(framing?.tablet, desktop);
  const phone = clean(framing?.phone, tablet);
  return { desktop, tablet, phone };
}

const hasAny = (framing) => !!framing && DEVICES.some((d) => framing[d] && Object.keys(framing[d]).length);

/**
 * Props for a framed <img>/<video> (object-fit: cover): { className, style } — or {} when the
 * photo has no framing. Merge with the element's own className/style:
 *   const fr = framingProps(media.framing);
 *   <img className={`cp-hero__media ${fr.className || ""}`} style={fr.style} … />
 * The element's parent must clip (overflow: hidden/clip) for zoom.
 */
export function framingProps(framing) {
  if (!hasAny(framing)) return {};
  const e = effectiveFraming(framing);
  const style = {};
  const classes = ["is-framed"];
  for (const [d, k] of [["desktop", "d"], ["tablet", "t"], ["phone", "p"]]) {
    style[`--fp-${k}`] = `${e[d].x}% ${e[d].y}%`;
    style[`--fz-${k}`] = String(e[d].zoom);
    // Shape classes only apply inside their own screen size (index.css), so a size with no
    // shape never picks up one from another size.
    if (e[d].shape) {
      classes.push(`shape-${k}`);
      style[`--fa-${k}`] = SHAPE_RATIO[e[d].shape];
    }
  }
  return { className: classes.join(" "), style };
}

/**
 * Where a photo's framing is stored, from the photo's CMS path (as used by useEd().img):
 * MEDIA objects keep it inside; list items {url} beside url; plain fields as `<name>Framing`.
 */
export function framingPath(path, { media = false } = {}) {
  const p = Array.isArray(path) ? path : String(path).split(".");
  if (media) return [...p, "framing"];
  const last = p[p.length - 1];
  if (last === "url") return [...p.slice(0, -1), "framing"];
  return [...p.slice(0, -1), `${last}Framing`];
}
